import { useCallback, useEffect, useState } from "react";
import { Check, Pencil, Plus, Trash2, UserRound, X } from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";

// Nomes alternativos (aliases) do atendente.
//
// A mesma pessoa atende por frentes diferentes — T.I., Telecom, Suporte — e
// para o cliente cada frente é uma empresa. Assinando sempre com o nome do
// cadastro, o cliente descobre que é a mesma pessoa nas três.
//
// Aqui se cadastram os nomes e se diz qual vale em cada fila. Trocar no meio
// de uma conversa é na própria conversa, pela barra de mensagem.
const AliasesDoUsuario = ({ userId, queueIds = [] }) => {
	const [aliases, setAliases] = useState([]);
	const [porFila, setPorFila] = useState({});
	const [novo, setNovo] = useState("");
	const [editando, setEditando] = useState(null);
	const [rascunho, setRascunho] = useState("");
	const [salvando, setSalvando] = useState(false);
	const [filas, setFilas] = useState([]);
	const { findAll: buscarFilas } = useQueues();

	const carregar = useCallback(
		(ativo = { current: true }) =>
			api
				.get("/user-aliases", { params: { userId } })
				.then(({ data }) => {
					if (!ativo.current) return;

					setAliases(data?.aliases || []);
					setPorFila(
						Object.fromEntries(
							(data?.porFila || []).map((v) => [v.queueId, v.aliasId]),
						),
					);
				})
				.catch(() => undefined),
		[userId],
	);

	useEffect(() => {
		if (!userId) return undefined;

		const ativo = { current: true };
		carregar(ativo);

		return () => {
			ativo.current = false;
		};
	}, [userId, carregar]);

	useEffect(() => {
		let ativo = true;

		buscarFilas()
			.then((lista) => {
				if (ativo) setFilas(lista || []);
			})
			.catch(() => undefined);

		return () => {
			ativo = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const adicionar = async () => {
		const nome = novo.trim();
		if (nome.length < 2) return;

		setSalvando(true);
		try {
			await api.post("/user-aliases", { userId, name: nome });
			setNovo("");
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const renomear = async (id) => {
		const nome = rascunho.trim();
		if (nome.length < 2) return;

		setSalvando(true);
		try {
			await api.put(`/user-aliases/${id}`, { userId, name: nome });
			setEditando(null);
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const excluir = async (alias) => {
		setSalvando(true);
		try {
			await api.delete(`/user-aliases/${alias.id}`, { params: { userId } });
			toast.success(`Alias "${alias.name}" removido.`);
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const definirDaFila = async (queueId, valor) => {
		const aliasId = valor === "none" ? null : Number(valor);

		// A tela reage na hora; a gravação vem atrás. Esperar o servidor para
		// mostrar a escolha faz o campo parecer travado.
		setPorFila((anterior) => ({ ...anterior, [queueId]: aliasId }));

		try {
			await api.put("/user-aliases/queue", { userId, queueId, aliasId });
		} catch (err) {
			toastError(err);
			carregar();
		}
	};

	if (!userId) {
		return (
			<div className="space-y-1.5 rounded-lg border border-dashed p-3">
				<Label className="flex items-center gap-2">
					<UserRound className="h-4 w-4 text-muted-foreground" />
					Nomes alternativos (aliases)
				</Label>
				<p className="text-xs text-muted-foreground">
					Salve o usuário primeiro para cadastrar os nomes alternativos.
				</p>
			</div>
		);
	}

	const filasDoUsuario = filas.filter((f) => queueIds.includes(f.id));

	return (
		<div className="space-y-3 rounded-lg border p-3">
			<div className="space-y-1">
				<Label className="flex items-center gap-2">
					<UserRound className="h-4 w-4 text-muted-foreground" />
					Nomes alternativos (aliases)
				</Label>
				<p className="text-xs text-muted-foreground">
					Crie nomes alternativos para usar em filas específicas. O alias
					aparece para o cliente na assinatura das mensagens.
				</p>
			</div>

			<div className="flex gap-2">
				<Input
					value={novo}
					onChange={(e) => setNovo(e.target.value)}
					placeholder="Novo alias"
					disabled={salvando}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							adicionar();
						}
					}}
				/>
				<Button
					type="button"
					size="icon"
					onClick={adicionar}
					disabled={salvando || novo.trim().length < 2}
					aria-label="Adicionar alias"
					title="Adicionar alias"
				>
					<Plus className="h-4 w-4" />
				</Button>
			</div>

			{aliases.length === 0 ? (
				<p className="text-xs text-muted-foreground">
					Nenhum alias cadastrado — as mensagens saem assinadas com o nome do
					cadastro.
				</p>
			) : (
				<ul className="divide-y rounded-lg border">
					{aliases.map((alias) => (
						<li
							key={alias.id}
							className="flex items-center gap-2 px-2 py-1.5 text-sm"
						>
							{editando === alias.id ? (
								<>
									<Input
										value={rascunho}
										onChange={(e) => setRascunho(e.target.value)}
										className="h-8"
										autoFocus
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												e.preventDefault();
												renomear(alias.id);
											}
											if (e.key === "Escape") setEditando(null);
										}}
									/>
									<Button
										type="button"
										size="icon"
										variant="ghost"
										className="h-8 w-8"
										onClick={() => renomear(alias.id)}
										aria-label="Salvar nome"
									>
										<Check className="h-4 w-4" />
									</Button>
									<Button
										type="button"
										size="icon"
										variant="ghost"
										className="h-8 w-8"
										onClick={() => setEditando(null)}
										aria-label="Cancelar"
									>
										<X className="h-4 w-4" />
									</Button>
								</>
							) : (
								<>
									<span className="flex-1 truncate">{alias.name}</span>
									<Button
										type="button"
										size="icon"
										variant="ghost"
										className="h-8 w-8 text-muted-foreground"
										onClick={() => {
											setEditando(alias.id);
											setRascunho(alias.name);
										}}
										aria-label={`Renomear ${alias.name}`}
									>
										<Pencil className="h-3.5 w-3.5" />
									</Button>
									<Button
										type="button"
										size="icon"
										variant="ghost"
										className="h-8 w-8 text-muted-foreground hover:text-destructive"
										onClick={() => excluir(alias)}
										disabled={salvando}
										aria-label={`Excluir ${alias.name}`}
									>
										<Trash2 className="h-3.5 w-3.5" />
									</Button>
								</>
							)}
						</li>
					))}
				</ul>
			)}

			{aliases.length > 0 && (
				<div className="space-y-1.5">
					<Label className="text-xs">Alias por fila</Label>

					{filasDoUsuario.length === 0 ? (
						<p className="text-xs text-muted-foreground">
							Escolha as filas do usuário para definir o alias de cada uma.
						</p>
					) : (
						<div className="grid gap-2 sm:grid-cols-2">
							{filasDoUsuario.map((fila) => (
								<div key={fila.id} className="space-y-1">
									<span className="text-xs text-muted-foreground">
										{fila.name}
									</span>
									<Select
										value={
											porFila[fila.id] ? String(porFila[fila.id]) : "none"
										}
										onValueChange={(v) => definirDaFila(fila.id, v)}
									>
										<SelectTrigger className="h-9">
											<SelectValue placeholder="Nome do cadastro" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="none">Nome do cadastro</SelectItem>
											{aliases.map((alias) => (
												<SelectItem key={alias.id} value={String(alias.id)}>
													{alias.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default AliasesDoUsuario;
